'use client';
import { cn } from '@wwe-portal/ui/lib/lib';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { TurnBackIcon } from '../icons/turn-back-icon';
import { Slot } from '@radix-ui/react-slot';
import { ClickableDiv } from '../button';

interface FlipCardContextValue {
  flip: boolean;
  setFlip: (flip: boolean) => void;
}

const FlipCardContext = createContext<FlipCardContextValue | null>(null);
const useFlipCardContext = () => {
  const context = useContext(FlipCardContext);
  if (!context) {
    throw new Error('FlipCardContext not found');
  }
  return context;
};

export const FlipCard: FC<{
  children: React.ReactNode;
  className?: string;
  flip?: boolean;
  onChangeValue?: (flip: boolean) => void;
}> = ({ children, className, flip: controlledValue, onChangeValue }) => {
  const [localFlipped, setLocalFlipped] = useState(controlledValue ?? false);

  const handleChangeValue = useCallback(
    (flip: boolean) => {
      setLocalFlipped(flip);
      onChangeValue?.(flip);
    },
    [onChangeValue]
  );

  const flip = useMemo(
    () => controlledValue ?? localFlipped,
    [controlledValue, localFlipped]
  );

  const value = useMemo(
    () => ({ flip, setFlip: handleChangeValue }),
    [flip, handleChangeValue]
  );

  return (
    <FlipCardContext.Provider value={value}>
      <div className={cn('flip-card', className)} data-flip={flip}>
        <div className="flip-card-inner" data-flip={flip}>
          {children}
        </div>
      </div>
    </FlipCardContext.Provider>
  );
};

export const FlipCardFront: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { flip } = useFlipCardContext();
  return (
    <div
      className={cn('flip-card-front relative h-full', {
        'pointer-events-none touch-none': flip,
      })}
    >
      {children}
    </div>
  );
};

export const FlipCardBack: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { flip } = useFlipCardContext();
  return (
    <div
      className={cn('flip-card-back', {
        'pointer-events-none touch-none': !flip,
      })}
    >
      {children}
    </div>
  );
};

export const FlipCardTrigger: FC<{
  children: ReactNode;
  asChild?: boolean;
}> = ({ children, asChild }) => {
  const { flip, setFlip } = useFlipCardContext();
  const Trigger = asChild ? Slot : ClickableDiv;
  return <Trigger onClick={() => setFlip(!flip)}>{children}</Trigger>;
};

export const FlipCardTurnButton: FC<{
  className?: string;
  iconClassName?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}> = ({ className, iconClassName, onClick }) => {
  return (
    <FlipCardTrigger asChild>
      <ClickableDiv className={className} onClick={onClick}>
        <TurnBackIcon
          className={cn('drop-shadow-card-2', iconClassName)}
          height={22}
          width={22}
        />
      </ClickableDiv>
    </FlipCardTrigger>
  );
};
